import { useEffect, useRef, useState } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Plus, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import {
  useOrgRoles,
  useOrgServiceCatalog,
  useCreateOrgRole,
  useUpdateOrgRole,
  useDeleteOrgRole,
} from '@/hooks/org-admin';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { RoleRow } from '@/components/molecules/org-admin/RoleRow';
import { RoleFormModal, type RoleFormSubmitData } from '@/components/molecules/org-admin/RoleFormModal';
import type { OrgAdminSelfContext } from '@/components/molecules/org-admin/types';
import type { OrgRole } from '@/services/org-admin';

export interface RolesPanelProps {
  self: OrgAdminSelfContext;
}

type ModalState = { mode: 'create' } | { mode: 'edit'; role: OrgRole } | null;

const SKELETON_ROWS = [
  { name: 'w-28', chips: 3 },
  { name: 'w-36', chips: 1 },
  { name: 'w-24', chips: 2 },
  { name: 'w-32', chips: 2 },
] as const;

function SkeletonRoleRow({ nameWidth, chips }: { nameWidth: string; chips: number }) {
  return (
    <div className="flex h-full flex-col rounded-lg border border-border/60 bg-card/50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className={`h-3.5 rounded-md bg-muted-foreground/10 loading-parallax ${nameWidth}`} />
        <div className="flex shrink-0 items-center gap-1">
          <div className="h-8 w-8 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
          <div className="h-8 w-8 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
        </div>
      </div>
      <div className="mt-3 flex flex-1 flex-col gap-1.5 rounded-md border border-border/40 bg-muted/20 p-2.5">
        <div className="h-2.5 w-12 rounded-md bg-muted-foreground/10 loading-parallax" />
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: chips }).map((_, i) => (
            <div key={i} className="h-5 w-16 rounded-full bg-muted-foreground/[0.07] loading-parallax" />
          ))}
        </div>
      </div>
    </div>
  );
}

export function RolesPanel({ self }: RolesPanelProps) {
  const { data: roles = [], isLoading } = useOrgRoles();
  const { data: serviceCatalog = [] } = useOrgServiceCatalog();
  const createRole = useCreateOrgRole();
  const updateRole = useUpdateOrgRole();
  const deleteRole = useDeleteOrgRole();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [modalState, setModalState] = useState<ModalState>(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && roles.length >= 0) {
      hasAnimatedRef.current = true;
    }
  }, [isLoading]);

  const trail = useTrail(roles.length, {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 320, friction: 28 },
    immediate: hasAnimatedRef.current,
  });

  const skeletonTrail = useTrail(SKELETON_ROWS.length, {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 320, friction: 28 },
  });

  const isSubmitting = createRole.isPending || updateRole.isPending;

  const handleFormSubmit = async (data: RoleFormSubmitData) => {
    try {
      if (modalState?.mode === 'edit') {
        await updateRole.mutateAsync({ roleId: modalState.role.id, input: data });
        toast.success('Role updated');
      } else {
        await createRole.mutateAsync(data);
        toast.success('Role created');
      }
      setModalState(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save role');
    }
  };

  const handleDelete = async (role: OrgRole) => {
    const confirmed = await confirmModal.error({
      title: 'Delete role?',
      content: `Deleting "${role.name}" is permanent. This isn't possible while any member is currently assigned to it — reassign them to another role first.`,
      okText: 'Delete',
      okButtonProps: { variant: 'destructive' },
    });
    if (!confirmed) return;
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success('Role deleted');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to delete role');
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Roles define which services a member can access.
        </p>
        <Button size="sm" onClick={() => setModalState({ mode: 'create' })}>
          <Plus className="h-4 w-4" />
          New role
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto border-t border-border/60">
        {isLoading ? (
          <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
            {skeletonTrail.map((style, index) => (
              <animated.div key={index} style={style}>
                <SkeletonRoleRow nameWidth={SKELETON_ROWS[index].name} chips={SKELETON_ROWS[index].chips} />
              </animated.div>
            ))}
          </div>
        ) : roles.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
              <ShieldCheck className="h-7 w-7 text-accent" />
            </div>
            <p>No roles yet — create one to start granting service access.</p>
          </div>
        ) : (
          <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
            {trail.map((style, index) => (
              <animated.div key={roles[index].id} style={style}>
                <RoleRow
                  role={roles[index]}
                  onEdit={(role) => setModalState({ mode: 'edit', role })}
                  onDelete={handleDelete}
                  isDeleting={deleteRole.isPending && deleteRole.variables === roles[index].id}
                />
              </animated.div>
            ))}
          </div>
        )}
      </div>

      <RoleFormModal
        open={modalState !== null}
        mode={modalState?.mode ?? 'create'}
        initialRole={modalState?.mode === 'edit' ? modalState.role : null}
        serviceCatalog={serviceCatalog}
        canGrantAdmin={self.currentUserRole === 'owner'}
        isSubmitting={isSubmitting}
        onSubmit={handleFormSubmit}
        onClose={() => setModalState(null)}
      />
      {confirmModalContext}
    </div>
  );
}
